import { Link } from '@tanstack/react-router'
import {
  resolveInventoryEventKindLabel,
  resolveInventoryEventTypeLabel,
} from './inventory-history-options'
import type { ColumnDef } from '@tanstack/react-table'
import type { InventoryHistoryItem } from '@/api/endpoints/inventory-history'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  addStockQuantities,
  formatStockDisplay,
} from '@/shared/lib/stock-quantity'

export type RestaurantLabelMap = Record<number, string>

// Whole quantities render exactly as before; decimals (CHG-2026-09-stock-usage-and-price-twins,
// D1) with the de-AT comma.
const formatNumber = (value?: number | null) =>
  value == null ? '-' : formatStockDisplay(value)

const formatDelta = (value?: number | null) => {
  if (value == null) return '-'
  if (value > 0) return `+${formatStockDisplay(value)}`
  return formatStockDisplay(value)
}

/** POS sale movements, whose reference is the receipt number. */
const RECEIPT_TYPES = new Set(['SALE_POS', 'VOID_POS', 'RETURN_POS'])

/**
 * The sold product behind a line, when it is not the stock article itself: a dish's part or a
 * variant's source (CHG-2026-09-stock-usage-and-price-twins, D7). A plain sale names its own SKU
 * and stays as it was.
 */
export const soldAsOf = (item: InventoryHistoryItem) => {
  if (!item.soldSku || item.soldSku === item.sku) return null
  return item.soldName ?? item.soldSku
}

const formatTimestamp = (value?: string | null) => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

const formatActor = (value?: string | null) => {
  if (!value) return '-'
  return value
}

const resolveActor = (
  tInventory: (key: string) => string,
  item: InventoryHistoryItem,
) => {
  if (item.operatorId) {
    const secondary =
      item.createdBy && item.createdBy !== item.operatorId
        ? item.createdBy
        : null
    return {
      primary: formatActor(item.operatorId),
      secondary: secondary ? formatActor(secondary) : null,
    }
  }
  if (item.createdBy === 'order-service') {
    return { primary: tInventory('history.actor.orderApp'), secondary: null }
  }
  if (item.createdBy && item.createdBy.startsWith('edge:')) {
    return {
      primary: tInventory('history.actor.selfCheckout'),
      secondary: null,
    }
  }
  if (item.createdBy === 'admin') {
    return { primary: tInventory('history.actor.backoffice'), secondary: null }
  }
  return { primary: formatActor(item.createdBy), secondary: null }
}

const resolveActorText = (
  tInventory: (key: string) => string,
  item: InventoryHistoryItem,
) => resolveActor(tInventory, item).primary

const orderIdPattern = /order:([0-9a-fA-F-]{36})/

/**
 * A booking writes exactly one RECEIVE movement with `reference = "delivery:<uuid>"`
 * (CHG-2026-09-announced-deliveries), which is how a stock line links back to the document that
 * created it.
 */
const deliveryIdPattern = /^delivery:([0-9a-fA-F-]{36})$/

export const resolveDeliveryIdFromHistory = (item: InventoryHistoryItem) => {
  const match = item.reference?.match(deliveryIdPattern)
  return match?.[1] ?? null
}

export const resolveOrderIdFromHistory = (item: InventoryHistoryItem) => {
  if (item.orderId) return item.orderId
  const candidates = [item.reference, item.note]
  for (const value of candidates) {
    if (!value) continue
    const match = value.match(orderIdPattern)
    if (match?.[1]) return match[1]
  }
  return null
}

const resolveSource = (
  tInventory: (key: string) => string,
  item: InventoryHistoryItem,
) => {
  const parts: Array<string> = []
  if (item.stationId) parts.push(item.stationId)
  if (item.hubId != null) parts.push(`hub:${item.hubId}`)
  if (parts.length) return parts.join(' / ')
  if (item.createdBy === 'order-service')
    return tInventory('history.source.orderApp')
  if (item.createdBy && item.createdBy.startsWith('edge:'))
    return tInventory('history.source.selfCheckout')
  return tInventory('history.source.backoffice')
}

const resolveRestaurantLabel = (
  item: InventoryHistoryItem,
  restaurantsById: RestaurantLabelMap,
) => {
  const restaurantId = item.restaurantId ?? null
  if (restaurantId == null) return '-'
  return restaurantsById[restaurantId] ?? String(restaurantId)
}

const resolveOnHandNumber = (item: InventoryHistoryItem) => {
  if (item.availableAfter == null && item.reservedAfter == null) return null
  return addStockQuantities(item.availableAfter, item.reservedAfter)
}

const resolveOnHand = (item: InventoryHistoryItem) => {
  const onHand = resolveOnHandNumber(item)
  return onHand == null ? '-' : formatStockDisplay(onHand)
}

const availableDelta = (item: InventoryHistoryItem) => {
  if (item.deltaQty == null) return null
  if (item.eventKind === 'reservation') return -item.deltaQty
  return item.deltaQty
}

const reservedDelta = (item: InventoryHistoryItem) => {
  if (item.eventKind !== 'reservation') return null
  return item.deltaQty ?? null
}

const deltaClass = (delta?: number | null) => {
  if (delta == null || delta === 0) return 'text-muted-foreground'
  return cn('font-semibold', delta > 0 ? 'text-emerald-700' : 'text-rose-700')
}

type InventoryHistoryColumnOptions = {
  onSelectOrder?: (orderId: string) => void
}

export const buildInventoryHistoryColumns = (
  tInventory: (key: string, options?: Record<string, unknown>) => string,
  restaurantsById: RestaurantLabelMap,
  options: InventoryHistoryColumnOptions = {},
): Array<ColumnDef<InventoryHistoryItem>> => [
  {
    accessorKey: 'createdAt',
    header: tInventory('history.columns.time'),
    size: 160,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {formatTimestamp(row.original.createdAt)}
      </span>
    ),
    meta: { label: tInventory('history.columns.time') },
  },
  {
    accessorKey: 'eventKind',
    header: tInventory('history.columns.kind'),
    size: 100,
    cell: ({ row }) => (
      <Badge
        variant={
          row.original.eventKind === 'reservation' ? 'secondary' : 'outline'
        }
      >
        {resolveInventoryEventKindLabel(tInventory, row.original.eventKind)}
      </Badge>
    ),
    meta: { label: tInventory('history.columns.kind') },
  },
  {
    accessorKey: 'eventType',
    header: tInventory('history.columns.type'),
    size: 110,
    cell: ({ row }) => (
      <Badge variant="outline">
        {resolveInventoryEventTypeLabel(tInventory, row.original.eventType)}
      </Badge>
    ),
    meta: { label: tInventory('history.columns.type') },
  },
  {
    accessorKey: 'sku',
    header: tInventory('history.columns.sku'),
    size: 100,
    cell: ({ row }) => <span className="font-mono">{row.original.sku}</span>,
    meta: { label: tInventory('history.columns.sku') },
  },
  {
    id: 'restaurantName',
    header: tInventory('history.columns.restaurant'),
    size: 120,
    accessorFn: (row) => resolveRestaurantLabel(row, restaurantsById),
    cell: ({ row }) => {
      return (
        <span>{resolveRestaurantLabel(row.original, restaurantsById)}</span>
      )
    },
    meta: { label: tInventory('history.columns.restaurant') },
  },
  {
    id: 'delta',
    header: tInventory('history.columns.delta'),
    size: 70,
    accessorFn: (row) => row.deltaQty ?? null,
    cell: ({ row }) => (
      <span className={cn('font-mono', deltaClass(row.original.deltaQty))}>
        {formatDelta(row.original.deltaQty)}
      </span>
    ),
    meta: { label: tInventory('history.columns.delta') },
  },
  {
    id: 'availableAfter',
    header: tInventory('history.columns.availShort'),
    size: 70,
    accessorFn: (row) => row.availableAfter ?? null,
    cell: ({ row }) => (
      <span
        className={cn('font-mono', deltaClass(availableDelta(row.original)))}
      >
        {formatNumber(row.original.availableAfter)}
      </span>
    ),
    meta: { label: tInventory('history.columns.available') },
  },
  {
    id: 'reservedAfter',
    header: tInventory('history.columns.resShort'),
    size: 60,
    accessorFn: (row) => row.reservedAfter ?? null,
    cell: ({ row }) => (
      <span
        className={cn('font-mono', deltaClass(reservedDelta(row.original)))}
      >
        {formatNumber(row.original.reservedAfter)}
      </span>
    ),
    meta: { label: tInventory('history.columns.reserved') },
  },
  {
    id: 'onHand',
    header: tInventory('history.columns.onHand'),
    size: 80,
    accessorFn: (row) => resolveOnHandNumber(row),
    cell: ({ row }) => (
      <span className="font-mono">{resolveOnHand(row.original)}</span>
    ),
    meta: { label: tInventory('history.columns.onHand') },
  },
  {
    id: 'actor',
    header: tInventory('history.columns.actor'),
    size: 110,
    accessorFn: (row) => resolveActorText(tInventory, row),
    cell: ({ row }) => {
      const actor = resolveActor(tInventory, row.original)
      return (
        <div>
          <div className="font-medium">{actor.primary}</div>
          {actor.secondary ? (
            <div className="text-xs text-muted-foreground">
              {actor.secondary}
            </div>
          ) : null}
        </div>
      )
    },
    meta: { label: tInventory('history.columns.actor') },
  },
  {
    id: 'source',
    header: tInventory('history.columns.source'),
    size: 100,
    accessorFn: (row) => resolveSource(tInventory, row),
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {resolveSource(tInventory, row.original)}
      </span>
    ),
    meta: { label: tInventory('history.columns.source') },
  },
  {
    accessorKey: 'reference',
    header: tInventory('history.columns.reference'),
    size: 130,
    cell: ({ row }) => {
      const orderId = resolveOrderIdFromHistory(row.original)
      const shortId = orderId ? orderId.slice(0, 8) : null
      const deliveryId = resolveDeliveryIdFromHistory(row.original)
      const soldAs = soldAsOf(row.original)
      const receipt =
        soldAs != null &&
        row.original.reference != null &&
        RECEIPT_TYPES.has(row.original.eventType)
      return (
        <div className="flex flex-col gap-1">
          {soldAs != null ? (
            <span className="text-sm">
              {row.original.eventType === 'WRITE_OFF'
                ? tInventory('history.takenAs')
                : tInventory('history.soldAs')}{' '}
              <strong className="font-semibold">{soldAs}</strong>
              {receipt
                ? ` · ${tInventory('history.receipt', { reference: row.original.reference })}`
                : null}
            </span>
          ) : null}
          {deliveryId ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="justify-start p-0 h-auto"
              asChild
            >
              <Link
                to="/inventory/deliveries/$id"
                params={{ id: deliveryId }}
                search={
                  row.original.restaurantId != null
                    ? { restaurantId: row.original.restaurantId }
                    : {}
                }
                onClick={(e) => e.stopPropagation()}
                title={deliveryId}
              >
                {tInventory('history.delivery', {
                  shortId: deliveryId.slice(0, 8),
                })}
              </Link>
            </Button>
          ) : null}
          {orderId && options.onSelectOrder ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="justify-start p-0 h-auto"
              onClick={(e) => {
                e.stopPropagation()
                options.onSelectOrder?.(orderId)
              }}
              title={orderId}
            >
              {tInventory('history.order', { shortId })}
            </Button>
          ) : null}
          {receipt ? null : (
            <span className="truncate">{row.original.reference ?? '-'}</span>
          )}
        </div>
      )
    },
    meta: { label: tInventory('history.columns.reference') },
  },
  {
    accessorKey: 'note',
    header: tInventory('history.columns.note'),
    size: 150,
    cell: ({ row }) => (
      <span className="block truncate" title={row.original.note ?? undefined}>
        {row.original.note ?? '-'}
      </span>
    ),
    meta: { label: tInventory('history.columns.note') },
  },
]
